/**
 * RetailerAccountsSection — the "Hierarchy, staff accounts & Salesforce" block
 * of the retailer edit modal:
 *
 *   - Parent retailer + "inherit parent pricing" (franchise locations under a
 *     national chain that either keep their own price book or use the parent's)
 *   - Email domains: staff registering with one of these domains are proposed
 *     as members of this retailer and queued for Trusscore approval
 *   - Salesforce accounts: which Salesforce Account ids ARE this retailer, so a
 *     Contact found by email resolves here
 *
 * Mirrors the server rules in admin-retailers.php / retailer-helper.php
 * loosely (the server is authoritative): consumer mailbox domains and the
 * Trusscore staff domain are refused, Salesforce ids must be 15 or 18 chars.
 */

import React, { useMemo, useState } from 'react';
import { Copy, Plus, Users, X } from 'lucide-react';
import { Tooltip } from '../../components/ui/Tooltip';
import { ChipListInput } from '../../components/admin/ChipListInput';
import type { AdminRetailer, RetailerSfAccount } from '../../services/adminRetailersApi';

interface Props {
  retailer: AdminRetailer;
  /** Every retailer, for the parent picker. May be empty while loading. */
  retailers: AdminRetailer[];
  isCreating: boolean;
  onChange: <K extends keyof AdminRetailer>(key: K, value: AdminRetailer[K]) => void;
}

/** Mirrors RT_CONSUMER_EMAIL_DOMAINS (abridged) — the server list is the one that enforces. */
const CONSUMER_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.ca', 'live.com', 'live.ca',
  'msn.com', 'yahoo.com', 'yahoo.ca', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'protonmail.com', 'proton.me', 'shaw.ca', 'rogers.com', 'bell.net', 'telus.net', 'sympatico.ca',
  'videotron.ca', 'cogeco.ca', 'eastlink.ca', 'sasktel.net', 'comcast.net', 'att.net', 'verizon.net',
]);
const STAFF_DOMAINS = new Set(['trusscore.com']);
const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export const normalizeDomain = (raw: string): string => {
  let d = raw.trim().toLowerCase();
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  d = d.replace(/[/?#].*$/, '');
  d = d.replace(/^@+/, '');
  if (d.startsWith('www.')) d = d.slice(4);
  return d.replace(/\.+$/, '');
};

export const validateDomain = (d: string): string | null => {
  if (!DOMAIN_RE.test(d)) return `"${d}" is not a valid domain — enter the part after the @, e.g. homehardware.ca`;
  if (STAFF_DOMAINS.has(d)) return `"${d}" is the Trusscore staff domain and cannot be attached to a retailer`;
  if (CONSUMER_DOMAINS.has(d)) return `"${d}" is a public mailbox provider and cannot identify a retailer`;
  return null;
};

const SF_ID_RE = /^[A-Za-z0-9]{15}([A-Za-z0-9]{3})?$/;

const DOMAINS_HELP =
  'Staff who register with an email address at one of these domains are proposed as members of this ' +
  'retailer and queued for Trusscore approval. Add every domain the retailer\'s staff use ' +
  '(e.g. homehardware.ca, swansonshh.ca). The same domain may appear on more than one retailer — ' +
  'a national brand and its independently owned locations, for example — in which case the approver picks.';

const SF_HELP =
  'The Salesforce Account id(s) that ARE this retailer. When someone registers, their email is looked up ' +
  'in Salesforce; if the Contact belongs to one of these Accounts they are proposed for this retailer. ' +
  'Tick "include child accounts" on a head-office / (Group) account to also match Contacts whose Account ' +
  'has that id as its Parent Account. Separate from the location-map filter, which only controls map pins.';

const PRICING_HELP =
  'A location that inherits pricing has no price book of its own: estimates on its portal (and shares sent ' +
  'to it) use the parent\'s prices. Turn it off to give this location its own book, seeded from the parent\'s ' +
  'prices with every row inactive and flagged for review.';

const CHILD_ACCESS_HELP =
  'In the retailer admin area (/retaileradmin), this retailer\'s MANAGERS also see the estimates, shares and ' +
  'analytics of every retailer that lists this one as its parent (one level down). Viewers never do. ' +
  'Leave it off unless head office is meant to oversee its locations.';

export const RetailerAccountsSection: React.FC<Props> = ({ retailer, retailers, isCreating, onChange }) => {
  const [sfDraft, setSfDraft] = useState('');
  const [sfError, setSfError] = useState<string | null>(null);

  const parentOptions = useMemo(
    () => retailers
      .filter(r => r.id !== 1 && r.id !== retailer.id && r.parentRetailerId !== retailer.id)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [retailers, retailer.id],
  );

  const parent = retailer.parentRetailerId !== null
    ? retailers.find(r => r.id === retailer.parentRetailerId) ?? null
    : null;
  const parentName = parent?.name ?? retailer.parentRetailerName ?? null;

  const sfAccounts = retailer.sfAccounts ?? [];
  const mapFilterIds = retailer.mapFilter?.ids ?? [];
  const sfIdSet = new Set(sfAccounts.map(a => a.sfAccountId));
  const copyableIds = mapFilterIds.filter(id => SF_ID_RE.test(id.trim()) && !sfIdSet.has(id.trim()));

  const addSfAccount = (rawId: string) => {
    const id = rawId.trim();
    if (!id) return;
    if (!SF_ID_RE.test(id)) { setSfError(`"${id}" is not a Salesforce id (15 or 18 characters)`); return; }
    if (sfIdSet.has(id)) { setSfDraft(''); setSfError(null); return; }
    onChange('sfAccounts', [...sfAccounts, { sfAccountId: id, sfAccountName: null, includeChildren: false }]);
    setSfDraft('');
    setSfError(null);
  };

  const patchSfAccount = (idx: number, patch: Partial<RetailerSfAccount>) => {
    onChange('sfAccounts', sfAccounts.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const removeSfAccount = (idx: number) => {
    onChange('sfAccounts', sfAccounts.filter((_, i) => i !== idx));
  };

  return (
    <div className="border rounded-3 p-3 mb-3 bg-light">
      <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
        <Users size={16} /> Hierarchy, staff accounts &amp; Salesforce
      </h6>
      <p className="text-muted small mb-3">
        Where this retailer sits in a chain, and how its staff get matched to it when they create an estimator account.
      </p>

      {/* --- Parent + pricing source ------------------------------------- */}
      <div className="row g-2 mb-3">
        <div className="col-sm-6">
          <label className="form-label small fw-semibold mb-1" htmlFor="ret-parent">Parent retailer</label>
          <select
            id="ret-parent"
            className="form-select form-select-sm"
            value={retailer.parentRetailerId ?? ''}
            onChange={e => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              onChange('parentRetailerId', v);
              if (v === null && retailer.pricingSource === 'parent') onChange('pricingSource', 'own');
            }}
          >
            <option value="">— none (top-level retailer) —</option>
            {parentOptions.map(r => (
              <option key={r.id} value={r.id}>{r.name}{r.pricingInherited ? ' (inherits pricing)' : ''}</option>
            ))}
          </select>
          <div className="form-text small">
            For franchise or branch locations: the national chain or group this retailer belongs to.
          </div>
        </div>
        <div className="col-sm-6 d-flex flex-column justify-content-end">
          <div className="form-check form-switch d-flex align-items-center gap-2 mb-1">
            <input
              id="ret-inherit-pricing"
              type="checkbox"
              className="form-check-input"
              role="switch"
              disabled={retailer.parentRetailerId === null}
              checked={retailer.pricingSource === 'parent'}
              onChange={e => onChange('pricingSource', e.target.checked ? 'parent' : 'own')}
            />
            <label className="form-check-label small fw-semibold" htmlFor="ret-inherit-pricing">
              Inherit parent&apos;s pricing
            </label>
            <Tooltip content={PRICING_HELP} position="left" />
          </div>
          <div className="form-text small">
            {retailer.parentRetailerId === null
              ? 'Pick a parent to enable.'
              : retailer.pricingSource === 'parent'
                ? <>Prices come from <strong>{parentName ?? 'the parent'}</strong>. {!isCreating && 'Its price book is shown read-only below.'}</>
                : 'This retailer keeps its own price book.'}
          </div>
        </div>
      </div>


      {/* --- Email domains ---------------------------------------------- */}
      <div className="mb-3">
        <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1">
          Email domains
          <Tooltip content={DOMAINS_HELP} position="right" />
        </label>
        <ChipListInput
          value={retailer.emailDomains ?? []}
          onChange={next => onChange('emailDomains', next)}
          placeholder="homehardware.ca"
          emptyText="No domains yet — staff won't be matched to this retailer by email domain."
          normalize={normalizeDomain}
          validate={validateDomain}
        />
      </div>

      {/* --- Salesforce accounts ---------------------------------------- */}
      <div>
        <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1">
          Salesforce accounts
          <Tooltip content={SF_HELP} position="right" />
        </label>

        {sfAccounts.length > 0 && (
          <table className="table table-sm mb-2 bg-white" style={{ maxWidth: 720 }}>
            <thead>
              <tr className="small text-muted">
                <th style={{ width: 200 }}>Account id</th>
                <th>Label (optional)</th>
                <th className="text-center" style={{ width: 150 }}>Include child accounts</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {sfAccounts.map((a, idx) => (
                <tr key={a.sfAccountId}>
                  <td className="font-monospace small align-middle">{a.sfAccountId}</td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Home Hardware (Group)"
                      maxLength={255}
                      value={a.sfAccountName ?? ''}
                      onChange={e => patchSfAccount(idx, { sfAccountName: e.target.value || null })}
                    />
                  </td>
                  <td className="text-center align-middle">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={a.includeChildren}
                      onChange={e => patchSfAccount(idx, { includeChildren: e.target.checked })}
                      title="Also match Contacts whose Account has this id as its Parent Account"
                    />
                  </td>
                  <td className="text-end align-middle">
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 text-danger"
                      aria-label={`Remove ${a.sfAccountId}`}
                      onClick={() => removeSfAccount(idx)}
                    >
                      <X size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="d-flex flex-wrap align-items-start gap-2">
          <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
            <input
              type="text"
              className={`form-control font-monospace${sfError ? ' is-invalid' : ''}`}
              placeholder="0012T00001vk3LVQAY"
              maxLength={18}
              value={sfDraft}
              onChange={e => { setSfDraft(e.target.value); if (sfError) setSfError(null); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSfAccount(sfDraft); } }}
            />
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
              disabled={!sfDraft.trim()}
              onClick={() => addSfAccount(sfDraft)}
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {copyableIds.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              title="Add every Salesforce id from this portal's location-map filter"
              onClick={() => onChange('sfAccounts', [
                ...sfAccounts,
                ...copyableIds.map(id => ({ sfAccountId: id.trim(), sfAccountName: null, includeChildren: false })),
              ])}
            >
              <Copy size={13} /> Copy {copyableIds.length} from map filter
            </button>
          )}
        </div>
        {sfError && <div className="text-danger small mt-1">{sfError}</div>}
        {sfAccounts.length === 0 && (
          <div className="form-text small">
            No Salesforce accounts yet — staff won&apos;t be matched to this retailer through Salesforce.
          </div>
        )}
      </div>
    </div>
  );
};

export default RetailerAccountsSection;
